import React, { useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import { YES_NO_OPTIONS } from '../../constants/listOptions';
import ConfirmDialog from '../../components/ConfirmDialog';
import Drawer from '../../components/Drawer';
import useEditRoute from '../../utils/useEditRoute';
import usePageTitle from '../../utils/usePageTitle';
import { productLinesService } from '../../services/productionService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

function ProductLinesList() {
    usePageTitle('Linhas');
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canProducts = can('Products');

    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingLine, setEditingLine] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
    });

    const list = useTableList({
        storageKey: 'product-lines',
        fetcher: (params) => productLinesService.list(params),
        errorMessage: 'Não foi possível carregar linhas',
    });

    const columns = useMemo(() => ([
        {
            key: 'name',
            label: 'Nome',
            className: 'font-medium',
        },
    ]), []);

    const filters = [
        { key: 'name', label: 'Nome', type: 'text' },
        { key: 'has_products', label: 'Possui produtos', type: 'select', options: YES_NO_OPTIONS },
    ];

    const openNew = () => {
        setEditingLine(null);
        setFormData({ name: '' });
        setDrawerOpen(true);
    };

    const openEdit = async (row) => {
        setFormLoading(true);
        try {
            const response = await productLinesService.get(row.id);
            const data = response.data?.data || response.data || row;
            setEditingLine(data);
            setFormData({ name: data.name || '' });
            setDrawerOpen(true);
        } catch (e) {
            console.error('Error loading line:', e);
            toastApiError(e, { title: 'Erro ao carregar', fallback: 'Não foi possível carregar a linha' });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteClick = (row) => {
        setDeleteDialog({ open: true, id: row.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await productLinesService.delete(deleteDialog.id);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({
                title: 'Linha excluída',
                description: 'O registro foi excluído com sucesso',
                variant: 'success',
            });
        } catch (e) {
            console.error('Error deleting line:', e);
            toastApiError(e, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir a linha' });
        }
    };

    const handleFormChange = (e) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        if (formLoading) return;
        setFormLoading(true);
        try {
            const payload = { name: formData.name };

            if (editingLine?.id) {
                const response = await productLinesService.update(editingLine.id, payload);
                const saved = response.data?.data || response.data;
                if (saved) setEditingLine(saved);
                toast({
                    title: 'Linha salva',
                    description: 'Alterações salvas com sucesso',
                    variant: 'success',
                });
            } else {
                const response = await productLinesService.create(payload);
                const saved = response.data?.data || response.data;
                if (saved) setEditingLine(saved);
                toast({
                    title: 'Linha criada',
                    description: 'Cadastro realizado com sucesso',
                    variant: 'success',
                });
            }

            list.reload();
        } catch (e) {
            console.error('Error saving line:', e);
            toastApiError(e, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar a linha' });
        } finally {
            setFormLoading(false);
        }
    };

    useEditRoute({
        basePath: '/production/lines',
        ready: list.loaded,
        drawerOpen: drawerOpen,
        open: id => openEdit({ id }),
    });

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Linhas"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção' },
                    ]}
                    actions={[
                        ...(canProducts ? [{
                            label: 'Nova Linha',
                            icon: 'new',
                            onClick: openNew,
                        }] : []),
                    ]}
                />

                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por nome..."
                    columns={columns}
                    editTo={canProducts ? (row) => `/production/lines/${row.id}` : undefined}
                    onEdit={canProducts ? openEdit : undefined}
                    onDelete={canProducts ? handleDeleteClick : undefined}
                    emptyMessage="Nenhuma linha cadastrada"
                />
            </div>

            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir Linha"
                message="Tem certeza que deseja excluir esta linha? Produtos vinculados ficarão sem linha. Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />

            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={editingLine?.id ? 'Editar Linha' : 'Nova Linha'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>
                            Cancelar
                        </Button>
                        <Button type="button" onClick={handleSave} disabled={formLoading || !canProducts}>
                            {formLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome *
                        </label>
                        <Input
                            name="name"
                            value={formData.name}
                            onChange={handleFormChange}
                            required
                        />
                    </div>
                </div>
            </Drawer>
        </AppLayout>
    );
}

export default ProductLinesList;
